import { useState, useEffect, useRef } from 'react';
import { useSalesAssistStore } from 'features/chatAssist/ChatAssist.store';
import { msgInfo, errorResponse } from '../../constants';
import {
  askQuestionListenStreamEvents,
  regenerateQuestionListenStreamEvents,
} from 'features/chatAssist/newServices/SSE/askQuestionStreamEvent';
import useGetConversationQuery from 'features/chatAssist/newServices/queries/useGetConversation';
import { AI_CHAT } from 'features/chatAssist/newServices/queries/queryKeys';
import { queryClient } from 'hocs/QueryWrapper';

const ChatAssistV3Logic = () => {
  const client_id = useSalesAssistStore((state) => state?.client_id);
  const conversation_id = useSalesAssistStore((state) => state?.conversation_id);
  const conversation = useSalesAssistStore((state) => state.conversation);
  const setConversation = useSalesAssistStore((state) => state.setConversation);
  const setConversationID = useSalesAssistStore((state) => state?.setConversationID);
  const addNewUserMessage = useSalesAssistStore((state) => state?.addNewUserMessage);
  const addAIResponseChunk = useSalesAssistStore((state) => state?.addAIResponseChunk);
  const addAIRegenerationResponseChunk = useSalesAssistStore(
    (state) => state?.addAIRegenerationResponseChunk
  );
  const messagesContainerRef = useRef(null);
  const firstQuestionRef = useRef(null);
  const firstResponseRef = useRef(null);

  const [value, setValue] = useState(''); //input value
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [isWaiting, setIsWaiting] = useState(false); //to handle the loading status of the request

  useGetConversationQuery({ conversationId: conversation_id, setConversation });

  const isJustSpaces = (m) => m.trim() === '';

  const onChange = (e) => {
    e.preventDefault();
    setValue(e.target.value);
  };

  const handleScroll = () => {
    const container = messagesContainerRef.current;
    if (container) {
      const { scrollTop } = container;

      if (scrollTop < 0) {
        setShowScrollButton(true);
      } else {
        setShowScrollButton(false);
      }
    }
  };

  const scrollToBottom = () => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  };

  const handleNewMessages = async (text) => {
    if (isJustSpaces(text)) return;

    addNewUserMessage(text);
    setValue('');
    setIsWaiting(true);

    const isFirstMessage = conversation.length < 2;

    await askQuestionListenStreamEvents({
      isFirstMessage,
      client_id,
      prompt: text,
      onMessage: (chunk) => {
        addAIResponseChunk(chunk);
      },
      onError: (err) => {
        console.error('Stream error:', err);
        addAIResponseChunk(errorResponse?.text);
      },
      onComplete: () => {
        queryClient.invalidateQueries({ queryKey: [AI_CHAT.CONVERSATION, conversation_id] });
      },
      onLoading: () => {
        setIsWaiting(false);
      },
      onSaveConversationID: (data) => {
        setConversationID(data);
      },
      conversation_id,
    });
  };

  const handleRegenerateResponse = async ({ message_id, regenerate_type, reason }) => {
    setIsWaiting(true);
    const messageIndex = conversation?.findIndex((c) => c.message_id === message_id);
    const copyArray = [...conversation];
    if (messageIndex >= 0) {
      copyArray.splice(0, messageIndex);
    }
    await regenerateQuestionListenStreamEvents({
      conversation_id,
      client_id,
      message_id,
      regenerate_type,
      reason,
      onStart: () => {
        if (messageIndex >= 0) {
          setConversation(
            copyArray?.map((c, index) =>
              message_id === c.message_id
                ? {
                    ...c,
                    content: '',
                    text: '',
                  }
                : { ...c }
            )
          );
        }
      },
      onMessage: (chunk) => {
        addAIRegenerationResponseChunk(chunk, message_id);
      },
      onError: () => {
        addAIRegenerationResponseChunk(errorResponse?.text, message_id);
      },
      onComplete: () => {
        queryClient.invalidateQueries({ queryKey: [AI_CHAT.CONVERSATION, conversation_id] });
      },
      onLoading: () => {
        queryClient.invalidateQueries({ queryKey: [AI_CHAT.CONVERSATION, conversation_id] });
        setIsWaiting(false);
      },
    });
  };

  const handleSendMessage = () => handleNewMessages(value, msgInfo?.sender);

  useEffect(() => {
    const container = messagesContainerRef.current;
    const scrollHandler = () => handleScroll();
    if (container) {
      container.addEventListener('scroll', scrollHandler);
      handleScroll();
    }
    return () => {
      if (container) {
        container.removeEventListener('scroll', scrollHandler);
      }
    };
  }, []);

  useEffect(() => {
    if (conversation.length === 0) return;

    const lastMessage = conversation[0];

    if (lastMessage.type === msgInfo.sender && firstQuestionRef.current) {
      firstQuestionRef.current.scrollIntoView({ block: 'start' });
    } else if (lastMessage.type === msgInfo.recipient && firstResponseRef.current) {
      firstResponseRef.current.scrollIntoView({ block: 'start' });
    }
  }, [conversation]);

  return {
    conversation,
    messagesContainerRef,
    firstQuestionRef,
    firstResponseRef,
    value,
    showScrollButton,
    isWaiting,
    isJustSpaces,
    onChange,
    scrollToBottom,
    handleSendMessage,
    conversation_id,
    client_id,
    handleRegenerateResponse,
  };
};

export default ChatAssistV3Logic;
